import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useTransactionHistoryDocuments } from '@pages/Traders-Room/Transaction-History/hooks/useTransactionHistoryDocuments'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { Operator, useApiClient } from '@utils/ApiClient'
import { SharedContext } from '@utils/SharedContext'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { getScrollToCardId } from '@utils/getItemId'
import { filterObjectByKeys } from '@utils/object-filter'

import { FilterQueryProps } from '@model/FilterFields'
import { TransactionDto } from '@model/TransactionDto'
import { WalletTypeEnum, isWalletETDType, isWalletFX_CFDType } from '@model/WalletDto'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
  useScrollToElementIds,
} from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { FirstTimeGuideCarousel } from '@shared/components/FirstTimeGuideCarousel/FirstTimeGuideCarousel'
import { Loading } from '@shared/components/Loading/Loading'
import { TransactionHistoryDrawerModal } from '@shared/components/TransactionHistoryDrawer'
import {
  TransactionHistoryEmptyState,
  TransactionHistoryMobileRow,
} from '@shared/components/TransactionHistoryTable'
import { ActionModal } from '@shared/modals/Action.modal'

import { TransactionHistoryTable } from './TransactionHistoryTable'

interface SearchProps extends FilterQueryProps {
  ca_search_WalletId?: string
  ca_search_TaId?: string
}

interface TransactionHistoryProps {
  walletType?: WalletTypeEnum
  walletId?: string
  tradingAccountId?: string
}

const buildClientWalletType = (accountId?: string, walletType?: WalletTypeEnum) =>
  accountId != null && walletType != null ? `${accountId}__${walletType}` : undefined

const NON_FILTER_SEARCH_KEYS: string[] = [
  'ca_search_TextSearch',
  'ca_search_WalletId',
  'ca_search_TaId',
]

export const TransactionHistoryPage: React.FC<TransactionHistoryProps> = ({
  walletType = WalletTypeEnum.CFD,
}) => {
  useScrollToTop()
  const { t } = useTranslation()
  const location = useLocation()
  const [sharedState, setSharedState] = useContext(SharedContext)
  const { isDefaultCFDProductType, product } = useProductReadContext()
  const { scrollIntoView } = useScrollToElementIds()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const locale = useSessionLanguage()
  const isMobile = useWindowResize()
  const { account } = useAccountReadContext()

  const [filterVisible, setFilterVisible] = useState(false)

  const { walletId, tradingAccountId } = location.state || {}

  const initialQuery = useMemo<SearchProps>(
    () => ({
      ca_search_TaId: tradingAccountId || '',
      ca_search_WalletId: walletId || '',
    }),
    [tradingAccountId, walletId]
  )
  const [search, setSearch] = useState<SearchProps>(initialQuery)
  useEffect(() => {
    setSearch(initialQuery)
  }, [walletId, tradingAccountId, initialQuery])

  const searchKey = useMemo(
    () => ({
      ...filterObjectByKeys(search),
      tradingAccountId,
      walletId,
      accountId: account?.id,
      walletType,
    }),
    [search, tradingAccountId, walletId, account?.id, walletType]
  )

  const { data, meta, setPageQuery, pageQuery, isFetching, forceRefresh, hasInitialResults } =
    usePaginatedQuery({
      queryKey: queryKeys.transactionHistory(searchKey, locale),
      queryFn: (pageQuery) =>
        apiClient.getTransactions({
          ...pageQuery,
          search: { ...filterObjectByKeys(search) },
          caller: 'ca',
          ca_search_ClientWalletType: buildClientWalletType(account?.id, walletType),
          ca_search_TaId: tradingAccountId || '',
          ca_search_WalletId: walletId || search?.ca_search_WalletId || '',
          languageId: locale,
        }),
    })

  const clientWalletType = buildClientWalletType(account?.id, walletType)

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.transactionHistoryFilterOptions(clientWalletType, locale),
    queryFn: () => apiClient.getTransactionHistoryFilterOptions(clientWalletType!, locale),
    enabled: !!account?.id && !!walletType && !!clientWalletType,
  })
  const confirmCancelTransactionModal = useModal(ActionModal, {
    title: t('Transactions.Cancel Transaction'),
    description: t('Transactions.Are you sure you want to cancel your transaction'),
  })
  const [selectedTransactionId, setSelectedTransactionId] = useState<string>()
  const drawerModal = useModal(TransactionHistoryDrawerModal, () => ({
    context: 'client' as const,
    transactions: data,
    walletId,
    onCancelTransaction: (txToCancel: TransactionDto) => {
      confirmCancelTransactionModal.open({
        onConfirm: () => onCancelTransaction(txToCancel),
      })
    },
  }))

  const generatedDocumentFile = useTransactionHistoryDocuments(data, walletId)

  const onCancelTransaction = async (transaction: TransactionDto) => {
    await apiClient.cancelTransaction(transaction.id)
    queryClient.invalidateQueries({ queryKey: ['transactionHistory'] })
    queryClient.invalidateQueries({ queryKey: ['wallet'] })
    queryClient.invalidateQueries({
      queryKey: queryKeys.transaction.statusHistory(transaction.id, locale),
    })
    confirmCancelTransactionModal.close()
    forceRefresh()
  }

  const openTransactionDrawer = (tx: TransactionDto) => {
    setSelectedTransactionId(tx.id)
    if (isMobile) {
      scrollIntoView([ScrollToIds.TradersRoomTransactionHistoryHeader], {
        waitBeforeScrolling: false,
        skipElementsViewportVisibility: true,
      })
    }
    drawerModal.open({
      initialTransactionId: tx.id,
    })
  }
  const isDrawerOpen = drawerModal.isOpen
  const reRenderDrawer = drawerModal.reRender

  useEffect(() => {
    if (isDrawerOpen) {
      reRenderDrawer()
    }
  }, [data, isDrawerOpen, reRenderDrawer])

  useEffect(() => {
    if (!isDrawerOpen) {
      setSelectedTransactionId(undefined)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (
      location.state?.tradingAccountId ||
      location.state?.walletId ||
      location.state?.hasSuccessfulWithdrawAction
    ) {
      scrollIntoView([ScrollToIds.TradersRoomTransactionHistoryHeader])
    }
  }, [location, scrollIntoView])

  useEffect(() => {
    ;(async () => {
      let { wallets } = sharedState
      const walletsResponse = await apiClient.getWallets()
      wallets = (walletsResponse ?? [])
        .filter((x) =>
          isDefaultCFDProductType()
            ? isWalletFX_CFDType(x.walletType.id)
            : isWalletETDType(x.walletType.id)
        )
        .map(({ id, name, currency, depositAction }) => ({ id, name, currency, depositAction }))

      setSharedState((current) => ({ ...current, wallets }))
    })()
  }, [product])

  useEffect(() => {
    ;(async () => {
      let { transactionStates, tradingAccounts } = sharedState

      const getTradingAccountsResponse = await apiClient.getTradingAccounts({
        search: {
          AccountGroupType: { value: 2, operator: Operator.EQUAL },
          Platform: { value: 3, operator: Operator.NOT_EQUAL },
          Status: { value: 1, operator: Operator.EQUAL },
        },
      })

      tradingAccounts = (getTradingAccountsResponse?.items ?? []).map((w) => ({
        id: w.id,
        name: w.name,
      }))

      transactionStates = await apiClient.getTransactionStates(locale)

      setSharedState((current) => ({ ...current, transactionStates, tradingAccounts }))
    })()
  }, [locale, product])

  const [referenceTerm, setReferenceTerm] = useState<string>('')
  const initialRender = useRef(true)
  useEffect(() => {
    if (!initialRender.current) {
      const timer = setTimeout(() => {
        setPageQuery?.({
          ...pageQuery,
          pageIndex: 1,
          ca_search_TextSearch: referenceTerm,
        })
      }, 500)
      return () => clearTimeout(timer)
    } else {
      initialRender.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceTerm])

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.TradersRoomTransactionHistoryHeader,
    isPaginationEntrySelected,
    isFetching,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(ScrollToIds.TradersRoomTransactionHistoryHeader, isFetching, meta?.pageSize)

  const activeFilters = useMemo(
    () =>
      Object.entries(search).filter(
        ([key, value]) =>
          !NON_FILTER_SEARCH_KEYS.includes(key) && value !== undefined && value !== ''
      ).length,
    [search]
  )
  const hasPaginationResults = hasInitialResults || !!data.length

  const handleSearchFilters = useCallback(
    (searchFilters?: FilterQueryProps) => {
      const next = filterObjectByKeys(searchFilters || {})
      setSearch({
        ...initialQuery,
        ...next,
      })
      setPageQuery?.({ ...pageQuery, pageIndex: 1 })
    },
    [initialQuery, pageQuery, setPageQuery]
  )

  const sortOptions = [
    { name: t('Transactions.Transaction Type'), id: 'TransactionType.name' },
    { name: t('Created Date'), id: 'CreatedDate' },
    { name: t('Transactions.Transaction State'), id: 'TransactionState.name' },
  ]

  const content = isMobile ? (
    !data.length && !isFetching ? (
      <TransactionHistoryEmptyState hasActiveFilters={activeFilters > 0 || !!referenceTerm} />
    ) : (
      <Loading fullWidth showLoadingIcon isLoading={isFetching}>
        {data.map((trans, index) => (
          <TransactionHistoryMobileRow
            key={trans.id}
            tx={trans}
            context='client'
            walletId={walletId}
            id={getScrollToCardId(index, data.length, 'transaction-history')}
            onClick={openTransactionDrawer}
          />
        ))}
      </Loading>
    )
  ) : (
    <TransactionHistoryTable
      data={data}
      pageKey={`${meta?.pageIndex ?? 1}:${meta?.pageSize ?? 'default'}`}
      pageQuery={pageQuery}
      isLoading={isFetching}
      setPageQuery={setPageQuery}
      activeFilters={activeFilters}
      hasSearch={!!referenceTerm}
      onRowClick={openTransactionDrawer}
      selectedTransactionId={selectedTransactionId}
      walletId={walletId}
    />
  )

  return (
    <>
      <FirstTimeGuideCarousel disabled />

      <PageSection
        id={ScrollToIds.TradersRoomTransactionHistoryHeader}
        title={t('TransactionHistory.Transaction History')}
        documentFile={generatedDocumentFile}
        pageQuery={pageQuery}
        setPageQuery={setPageQuery}
        sortOptions={sortOptions}
        searchField={{
          label: t('Search reference'),
          onChange: (v) => setReferenceTerm(v),
          value: referenceTerm,
          canClear: true,
          isLoading: isFetching,
          renderResult: () => content,
        }}
        onFilterClick={() => setFilterVisible((s) => !s)}
      />

      <TableFilter
        isVisible={filterVisible}
        searchFilters={search}
        filterOptions={[
          FilterOptions.TransactionType,
          FilterOptions.TransactionStatus,
          FilterOptions.Date,
          FilterOptions.Amount,
          FilterOptions.WalletCurrencyId,
          FilterOptions.TradingAccountPlatformType,
          FilterOptions.TradingAccount,
        ]}
        transactionTypes={filterOptions?.transactionTypes}
        transactionStates={filterOptions?.transactionStates}
        walletCurrencyOptions={filterOptions?.walletCurrencies}
        platformTypes={filterOptions?.platformTypes}
        tradingAccounts={filterOptions?.tradingAccounts}
        tradingAccountSearchProps={{ options: filterOptions?.tradingAccounts || [] }}
        customLabels={{
          [FilterOptions.Date]: t('Date'),
          [FilterOptions.Amount]: t('Amount'),
          [FilterOptions.TransactionType]: t('Type'),
          [FilterOptions.TransactionStatus]: t('Status'),
          [FilterOptions.WalletCurrencyId]: t('Currency'),
          [FilterOptions.TradingAccountPlatformType]: t('Trading Account.Platform Type'),
          [FilterOptions.TradingAccount]: t('Trading Account.Trading Account'),
        }}
        setSearchFilters={handleSearchFilters}
      />

      {content}

      {meta && hasPaginationResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.TradersRoomTransactionHistoryHeader}
          maxPageSize={100}
          pageData={meta}
          isLoading={isFetching}
          onPageChanged={(pageIndex: number, pageSize: number) => {
            if (isMobile) {
              scrollIntoView([ScrollToIds.TradersRoomTransactionHistoryHeader], {
                waitBeforeScrolling: false,
                skipElementsViewportVisibility: true,
              })
            }
            setPageQuery?.({ ...pageQuery, pageIndex, pageSize })
          }}
        />
      )}
    </>
  )
}
